/**
 * Invite an Account Tray operator by EMAIL (ADR-0077 S3).
 *
 * The central door's twin of the "New user" dialog: instead of typing a
 * password, the admin types an email — Control creates-or-finds the central
 * identity, mails the claim link when a password must be chosen, and the
 * membership row is created password-less. The dialog then shows the OUTCOME:
 * the copyable claim link is the guarantee (the mail is the convenience), so
 * the admin can always hand it over on another channel.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { usersApi, type InviteUserOut } from "@/lib/api-users";

export function InviteUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [groups, setGroups] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  // Filled on success — the dialog flips to the outcome view (claim link).
  const [result, setResult] = React.useState<InviteUserOut | null>(null);
  const [copied, setCopied] = React.useState(false);

  // Reset the form each time the dialog opens fresh.
  React.useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setGroups([]);
      setError(null);
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  const groupsQ = useQuery({
    queryKey: ["groups", "invite-picker"],
    queryFn: () => usersApi.listGroups({ page: 1, page_size: 100 }),
    enabled: open,
  });

  const invite = useMutation({
    mutationFn: () =>
      usersApi.invite({
        email: email.trim(),
        full_name: fullName.trim() || null,
        groups,
      }),
    onSuccess: (res) => {
      setResult(res);
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message.includes("identity_invite:user_exists"))
        setError(t("users.inviteExists", "Esiste già un utente con questa email."));
      else if (
        err instanceof ApiError &&
        err.message.includes("identity_invite:central_login_unavailable")
      )
        setError(
          t(
            "users.inviteUnavailable",
            "Gli inviti Account Tray sono disponibili solo sui workspace SaaS.",
          ),
        );
      else setError(err instanceof ApiError ? err.message : "Unexpected error");
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    invite.mutate();
  }

  async function copyClaim() {
    if (!result?.claim_url) return;
    await navigator.clipboard.writeText(result.claim_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {result === null ? (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>{t("users.inviteTitle", "Invita con Account Tray")}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  "users.inviteHelp",
                  "La persona accede con il suo Account Tray: se non ne ha uno riceve un link per scegliere la password. Nessuna password locale in questo workspace.",
                )}
              </p>
              {error && <Alert tone="danger">{error}</Alert>}
              <div className="space-y-1.5">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-full-name">{t("users.fullName", "Nome completo")}</Label>
                <Input
                  id="invite-full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {(groupsQ.data?.items.length ?? 0) > 0 && (
                <div className="space-y-1.5">
                  <Label>{t("users.groups", "Gruppi")}</Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {groupsQ.data!.items.map((g) => (
                      <label
                        key={g.group_id}
                        className="flex items-center gap-1.5 text-sm text-foreground"
                      >
                        <input
                          type="checkbox"
                          checked={groups.includes(g.group_id)}
                          onChange={(e) =>
                            setGroups((curr) =>
                              e.target.checked
                                ? [...curr, g.group_id]
                                : curr.filter((id) => id !== g.group_id),
                            )
                          }
                        />
                        {g.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={invite.isPending || !email.trim()}>
                {t("users.inviteConfirm", "Invita")}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("users.invitedTitle", "Invito creato")}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {result.mode === "linked" ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "users.invitedLinked",
                    "Questa persona ha già un Account Tray: il workspace è stato aggiunto al suo account, nessuna password da impostare.",
                  )}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {result.mail_sent
                    ? t(
                        "users.invitedMailed",
                        "Invito inviato via email. In alternativa puoi consegnare a mano il link qui sotto per scegliere la password.",
                      )
                    : t(
                        "users.invitedNoMail",
                        "L'email di piattaforma non è configurata: consegna a mano il link qui sotto per scegliere la password.",
                      )}
                </p>
              )}
              {result.claim_url && (
                <div className="space-y-1.5">
                  <Label>{t("users.claimLink", "Link di attivazione")}</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={result.claim_url} className="font-mono text-xs" />
                    <Button type="button" variant="outline" size="icon" onClick={copyClaim}>
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />
                      ) : (
                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t("common.close", "Chiudi")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
